import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Building2, Users, Settings, Loader2, Search, 
  Edit, Shield, ToggleLeft, Mail, Phone, MapPin,
  Globe, Calendar, CreditCard, Zap
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface Organization {
  id: string;
  name: string;
  slug: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  company_website: string | null;
  company_tagline: string | null;
  company_description: string | null;
  subscription_status: string | null;
  pricing_tier_id: string | null;
  created_at: string;
  stripe_subscription_id: string | null;
  pricing_tiers?: {
    tier_name: string;
  } | null;
  _count: {
    users: number;
  };
}

interface FeatureFlag {
  id: string;
  org_id: string;
  feature_key: string;
  enabled: boolean;
}

interface PricingTier {
  id: string;
  tier_name: string;
  max_jobs_per_month: number | null;
}

const DEFAULT_FEATURES = [
  { key: "ai_assistant", label: "AI Assistant", description: "Enable AI-powered features" },
  { key: "email_campaigns", label: "Email Campaigns", description: "Automated email sequences" },
  { key: "phone_integration", label: "Phone Integration", description: "RingCentral calling features" },
  { key: "advanced_analytics", label: "Advanced Analytics", description: "Detailed reporting & insights" },
  { key: "custom_branding", label: "Custom Branding", description: "White-label options" },
  { key: "api_access", label: "API Access", description: "External integrations" },
  { key: "voice_agent", label: "Voice Agent", description: "ElevenLabs voice features" },
  { key: "supplier_integrations", label: "Supplier Integrations", description: "ABC Supply, SRS, etc." },
];

export function OrgOnboardingPanel() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    company_website: "",
    company_tagline: "",
    company_description: "",
    subscription_status: "",
    pricing_tier_id: "",
  });

  useEffect(() => {
    loadOrganizations();
    loadPricingTiers();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrgs(organizations);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredOrgs(
        organizations.filter((org) =>
          org.name.toLowerCase().includes(query) ||
          (org.email?.toLowerCase() || "").includes(query) ||
          (org.subscription_status?.toLowerCase() || "").includes(query)
        )
      );
    }
  }, [searchQuery, organizations]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const { data: orgs, error } = await supabase
        .from("organizations")
        .select(`
          *,
          pricing_tiers (tier_name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get user counts
      const orgsWithCounts = await Promise.all(
        (orgs || []).map(async (org) => {
          const { count } = await supabase
            .from("profiles")
            .select("id", { count: "exact", head: true })
            .eq("org_id", org.id);
          
          return {
            ...org,
            _count: { users: count || 0 },
          };
        })
      );

      setOrganizations(orgsWithCounts);
      setFilteredOrgs(orgsWithCounts);
    } catch (error: any) {
      console.error("Failed to load organizations:", error);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const loadPricingTiers = async () => {
    const { data, error } = await supabase
      .from("pricing_tiers")
      .select("*")
      .order("tier_name");
    
    if (!error && data) {
      setPricingTiers(data);
    }
  };

  const loadFeatureFlags = async (orgId: string) => {
    const { data, error } = await supabase
      .from("org_feature_flags")
      .select("*")
      .eq("org_id", orgId);
    
    if (!error) {
      setFeatureFlags(data || []);
    }
  };

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setEditFormData({
      name: org.name,
      email: org.email || "",
      phone: org.phone || "",
      address: org.address || "",
      city: org.city || "",
      state: org.state || "",
      zip: org.zip || "",
      company_website: org.company_website || "",
      company_tagline: org.company_tagline || "",
      company_description: org.company_description || "",
      subscription_status: org.subscription_status || "trial",
      pricing_tier_id: org.pricing_tier_id || "",
    });
    loadFeatureFlags(org.id);
  };

  const handleSaveOrg = async () => {
    if (!selectedOrg) return;
    
    try {
      setSaving(true);
      const { error } = await supabase
        .from("organizations")
        .update({
          name: editFormData.name,
          email: editFormData.email || null,
          phone: editFormData.phone || null,
          address: editFormData.address || null,
          city: editFormData.city || null,
          state: editFormData.state || null,
          zip: editFormData.zip || null,
          company_website: editFormData.company_website || null,
          company_tagline: editFormData.company_tagline || null,
          company_description: editFormData.company_description || null,
          subscription_status: editFormData.subscription_status,
          pricing_tier_id: editFormData.pricing_tier_id || null,
        })
        .eq("id", selectedOrg.id);

      if (error) throw error;

      toast.success("Organization updated successfully");
      loadOrganizations();
    } catch (error: any) {
      console.error("Failed to update organization:", error);
      toast.error("Failed to update organization");
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = async (featureKey: string, enabled: boolean) => {
    if (!selectedOrg) return;

    try {
      const existingFlag = featureFlags.find((f) => f.feature_key === featureKey);

      if (existingFlag) {
        await supabase
          .from("org_feature_flags")
          .update({ enabled })
          .eq("id", existingFlag.id);
      } else {
        await supabase.from("org_feature_flags").insert({
          org_id: selectedOrg.id,
          feature_key: featureKey,
          enabled,
        });
      }

      toast.success(`Feature ${enabled ? "enabled" : "disabled"}`);
      loadFeatureFlags(selectedOrg.id);
    } catch (error: any) {
      console.error("Failed to toggle feature:", error);
      toast.error("Failed to update feature");
    }
  };

  const isFeatureEnabled = (featureKey: string) => {
    const flag = featureFlags.find((f) => f.feature_key === featureKey);
    return flag?.enabled ?? true; // Default to enabled
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Organization List */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Organizations
          </CardTitle>
          <CardDescription>
            Select an organization to configure
          </CardDescription>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent className="max-h-[600px] overflow-y-auto">
          <div className="space-y-2">
            {filteredOrgs.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelectOrg(org)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedOrg?.id === org.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{org.name}</span>
                  <Badge variant={org.subscription_status === "active" ? "default" : "secondary"} className="text-xs">
                    {org.subscription_status || "trial"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                  <Users className="w-3 h-3" />
                  <span>{org._count.users} users</span>
                  <span>•</span>
                  <span>{org.pricing_tiers?.tier_name || "No tier"}</span>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organization Details */}
      <Card className="lg:col-span-2">
        {selectedOrg ? (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {selectedOrg.name}
              </CardTitle>
              <CardDescription>
                Created {format(new Date(selectedOrg.created_at), "MMM d, yyyy")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="details" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="details">Company Details</TabsTrigger>
                  <TabsTrigger value="subscription">Subscription</TabsTrigger>
                  <TabsTrigger value="features">Feature Flags</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Organization Name</Label>
                      <Input
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        placeholder="contact@company.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={editFormData.phone}
                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input
                        value={editFormData.company_website}
                        onChange={(e) => setEditFormData({ ...editFormData, company_website: e.target.value })}
                        placeholder="https://company.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input
                      value={editFormData.company_tagline}
                      onChange={(e) => setEditFormData({ ...editFormData, company_tagline: e.target.value })}
                      placeholder="Your trusted roofing partner"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <textarea
                      className="w-full min-h-[100px] px-3 py-2 border rounded-md bg-background"
                      value={editFormData.company_description}
                      onChange={(e) => setEditFormData({ ...editFormData, company_description: e.target.value })}
                      placeholder="Tell us about the company..."
                    />
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Address
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Street Address</Label>
                        <Input
                          value={editFormData.address}
                          onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={editFormData.city}
                          onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input
                            value={editFormData.state}
                            onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>ZIP</Label>
                          <Input
                            value={editFormData.zip}
                            onChange={(e) => setEditFormData({ ...editFormData, zip: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveOrg} disabled={saving}>
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="subscription" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pricing Tier</Label>
                      <Select
                        value={editFormData.pricing_tier_id}
                        onValueChange={(value) => setEditFormData({ ...editFormData, pricing_tier_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                        <SelectContent>
                          {pricingTiers.map((tier) => (
                            <SelectItem key={tier.id} value={tier.id}>
                              {tier.tier_name}
                              {tier.max_jobs_per_month && ` (${tier.max_jobs_per_month} jobs/mo)`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subscription Status</Label>
                      <Select
                        value={editFormData.subscription_status}
                        onValueChange={(value) => setEditFormData({ ...editFormData, subscription_status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trial">Trial</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="past_due">Past Due</SelectItem>
                          <SelectItem value="canceled">Canceled</SelectItem>
                          <SelectItem value="paused">Paused</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 bg-muted/50 space-y-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      <span className="font-medium">Stripe Details</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Subscription ID: <code className="bg-background px-2 py-1 rounded">{selectedOrg.stripe_subscription_id || "Not connected"}</code>
                    </p>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveOrg} disabled={saving}>
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="features" className="space-y-4">
                  <div className="border rounded-lg divide-y">
                    {DEFAULT_FEATURES.map((feature) => (
                      <div key={feature.key} className="flex items-center justify-between p-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-primary" />
                            <span className="font-medium">{feature.label}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                        <Switch
                          checked={isFeatureEnabled(feature.key)}
                          onCheckedChange={(checked) => toggleFeature(feature.key, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </>
        ) : (
          <CardContent className="flex items-center justify-center py-24">
            <div className="text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">Select an organization to manage</p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
